import { TargetType, PredictionCategory } from './chartTypes';

// Helper function to determine prediction category from target type
export const getPredictionCategory = (target: TargetType): PredictionCategory => {
  const COMMODITIES: TargetType[] = ['copper', 'crude'];

  if (COMMODITIES.includes(target)) return 'commodity';
  return 'electricity';
};

// Darken RGBA color
export const darkenRgba = (rgba: string, factor: number = 0.7): string => {
  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (!match) return rgba;
  const r = Math.max(0, Math.min(255, Math.round(parseInt(match[1], 10) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(match[2], 10) * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(match[3], 10) * factor)));
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Lighten RGBA color
export const lightenRgba = (rgba: string, factor: number = 1.3): string => {
  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (!match) return rgba;
  const r = Math.max(0, Math.min(255, Math.round(parseInt(match[1], 10) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(match[2], 10) * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(match[3], 10) * factor)));
  const a = match[4] !== undefined ? parseFloat(match[4]) * 0.7 : 0.7; // Also reduce opacity
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Get commodity color (with dark mode variants for colors that are too dark)
export const getCommodityColor = (commodity: TargetType, darkMode: boolean = false): string => {
  switch (commodity) {
    case 'copper':
      return darkMode ? 'rgba(212, 148, 85, 1)' : 'rgba(184, 115, 51, 1)';
    case 'crude':
      return darkMode ? 'rgba(148, 163, 184, 1)' : 'rgba(17, 24, 39, 1)'; // Slate gray in dark mode
    case 'electricity':
    default:
      return darkMode ? 'rgba(96, 165, 250, 1)' : 'rgba(13, 110, 253, 1)';
  }
};

// Calculate weighted prediction for electricity (3 types).
// Values may be null when a model didn't produce a point for this timestamp —
// e.g. the daily (days 8–30) segment has regression + news but no weather, since
// weather forecasts don't reach that far. We renormalize the weights over the
// components actually present so a missing model doesn't drag the blend toward
// zero. Returns null only when no component is available at all.
export const calculateWeightedPredictionElectricity = (
  weatherValue: number | null,
  regressionValue: number | null,
  newsValue: number | null,
  weights: { weather: number; regression: number; news: number }
): number | null => {
  const parts: { value: number; weight: number }[] = [];
  if (weatherValue !== null) parts.push({ value: weatherValue, weight: weights.weather });
  if (regressionValue !== null) parts.push({ value: regressionValue, weight: weights.regression });
  if (newsValue !== null) parts.push({ value: newsValue, weight: weights.news });
  if (parts.length === 0) return null;

  const weightSum = parts.reduce((acc, p) => acc + p.weight, 0);
  // If the present components all have zero weight, fall back to a plain mean so
  // we still render a point rather than dropping out.
  if (weightSum === 0) {
    return parts.reduce((acc, p) => acc + p.value, 0) / parts.length;
  }
  return parts.reduce((acc, p) => acc + p.value * p.weight, 0) / weightSum;
};

// Calculate weighted prediction for commodities (2 types: regression and news).
// Values may be null when a model is missing for this timestamp; the present
// component is then used on its own. Returns null when neither is available.
export const calculateWeightedPredictionCommodity = (
  regressionValue: number | null,
  newsValue: number | null,
  weight: number
): number | null => {
  // weight is 0-10, where 0 = all regression, 10 = all news
  const newsWeight = weight / 10;
  const regressionWeight = 1 - newsWeight;
  if (regressionValue === null && newsValue === null) return null;
  if (regressionValue === null) return newsValue;
  if (newsValue === null) return regressionValue;
  return (regressionValue * regressionWeight + newsValue * newsWeight);
};

// Collapse an actual-price series to 4-hourly means for the 30-day view, so the
// history line shares the same 4-hourly cadence as the (server-aggregated)
// 30-day forecast — keeping the category axis uniform. Each bucket is stamped at
// its 4-hour bin start in UTC (00,04,08,12,16,20), aligned to midnight via epoch
// math so it lines up exactly with the forecast's 4-hourly points.
const FOUR_HOUR_MS = 4 * 60 * 60 * 1000;
export const aggregatePricesTo4Hourly = (
  series: { timestamp: string; value: number }[],
): { timestamp: string; value: number }[] => {
  const buckets = new Map<number, { sum: number; count: number }>();
  for (const p of series) {
    // Price values arrive as strings from the API (Postgres numeric → string).
    // Coerce to Number before summing — otherwise `+=` concatenates strings and
    // every mean becomes NaN, which Chart.js silently drops (the history line
    // vanishes in the 30-day view).
    const value = Number(p.value);
    if (!Number.isFinite(value)) continue;
    const binStart = Math.floor(new Date(p.timestamp).getTime() / FOUR_HOUR_MS) * FOUR_HOUR_MS;
    const b = buckets.get(binStart);
    if (b) { b.sum += value; b.count += 1; }
    else buckets.set(binStart, { sum: value, count: 1 });
  }
  return Array.from(buckets.entries())
    .map(([binStart, b]) => ({ timestamp: new Date(binStart).toISOString(), value: b.sum / b.count }))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
};

// Calculate time span for display
export const calculateTimeSpan = (targetTimes: string[], language: string): string => {
  if (targetTimes.length === 0) return language === 'sv' ? 'Ingen data' : 'No data';
  const startTime = new Date(targetTimes[0]);
  const endTime = new Date(targetTimes[targetTimes.length - 1]);
  const diffInHours = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60));
  if (diffInHours < 24) {
    return language === 'sv' ? `${diffInHours} timmar` : `${diffInHours} hours`;
  } else {
    const days = Math.round(diffInHours / 24);
    return language === 'sv' ? `${days} dagar` : `${days} days`;
  }
};
